import type { TFunction } from 'i18next';
import type {
  PortalPaymentMethod,
  PortalSubscription,
  PortalSubscriptionLineItem,
} from '@api/customer-portal-api';
import { apiErrorMessage } from '@i18n/apiError';
import { frequencyFromDays } from './frequencyOffer';
import { parseUtcDate, titleCase } from './portalFormat';
import {
  getSubscriptionDisplayStatus,
  isEndedSubscriptionDisplayStatus,
} from './subscriptionStatus';

const DAY_MS = 24 * 60 * 60 * 1000;

// Older backends send only the singular line_item.
export const subscriptionItems = (sub: PortalSubscription): PortalSubscriptionLineItem[] => {
  if (Array.isArray(sub.line_items) && sub.line_items.length > 0) return sub.line_items;
  return sub.line_item ? [sub.line_item] : [];
};

export const lastChargedDate = (sub: PortalSubscription): string | null =>
  sub.last_payment_date || sub.start_date || null;

export type NextOrderState =
  | { kind: 'next'; date: string | null }
  | { kind: 'cancels'; date: string | null }
  | { kind: 'paused'; resumesAt: string; nextOrder: string | null }
  | { kind: 'pausedIndefinitely' }
  | { kind: 'ended'; expired: boolean; date: string | null };

// Same precedence as the dashboard card: ended, then paused, then cancel pending. Paused
// beats cancel pending because Whop sets cancel_at_period_end on pause.
export const nextOrderState = (sub: PortalSubscription): NextOrderState => {
  const status = getSubscriptionDisplayStatus(sub.status, false);
  if (isEndedSubscriptionDisplayStatus(status)) {
    return {
      kind: 'ended',
      expired: status === 'expired',
      date: sub.cancel_date ?? sub.end_date ?? null,
    };
  }
  if (status === 'paused') {
    if (!sub.pause_resumes_at) return { kind: 'pausedIndefinitely' };
    // The stored next_payment_date predates the pause, so the next order is derived from the
    // resume date instead: the charge lands the day after it. Added in UTC, where a day is
    // always 24h, so a DST change can't land it on the same calendar date.
    const resumes = parseUtcDate(sub.pause_resumes_at);
    return {
      kind: 'paused',
      resumesAt: sub.pause_resumes_at,
      nextOrder: resumes ? new Date(resumes.getTime() + DAY_MS).toISOString() : null,
    };
  }
  if (sub.cancel_at_period_end) return { kind: 'cancels', date: sub.next_payment_date ?? null };
  return { kind: 'next', date: sub.next_payment_date ?? null };
};

// can_pause carries the backend's other refusals (no Whop membership, a frequency change
// being confirmed); an older backend omits it and the local rule decides alone.
export const canPauseSubscription = (sub: PortalSubscription): boolean =>
  getSubscriptionDisplayStatus(sub.status, false) === 'active' &&
  !sub.cancel_at_period_end &&
  sub.can_pause !== false;

export const canCancelSubscription = (sub: PortalSubscription): boolean => {
  const status = getSubscriptionDisplayStatus(sub.status, false);
  if (isEndedSubscriptionDisplayStatus(status)) return false;
  return status === 'paused' || !sub.cancel_at_period_end;
};

// The unit comes from the plan's own period, not the total days, so a 45-day plan reads
// "2 cycles" rather than a rounded "3 months".
export const pauseLengthLabel = (
  t: TFunction,
  cycles: number,
  billingPeriodDays: number,
): string => {
  const period = frequencyFromDays(billingPeriodDays);
  if (period?.unit === 'weeks') return t('pause.length_weeks', { count: cycles * period.value });
  if (period?.unit === 'months') return t('pause.length_months', { count: cycles * period.value });
  return t('pause.length_cycles', { count: cycles });
};

export const PAUSE_CONFLICT_CODES = [
  'already_paused',
  'not_active',
  'cancel_pending',
  'no_membership',
  'frequency_change_pending',
  'pause_in_progress',
] as const;

type PauseConflictCode = (typeof PAUSE_CONFLICT_CODES)[number];

const isPauseConflictCode = (detail: unknown): detail is PauseConflictCode =>
  PAUSE_CONFLICT_CODES.some((code) => code === detail);

export const pauseErrorMessage = (t: TFunction, err: unknown): string => {
  const response = (err as { response?: { status?: number; data?: { detail?: unknown } } } | null)
    ?.response;
  const detail = response?.data?.detail;
  if (response?.status === 409) {
    return isPauseConflictCode(detail)
      ? t(`pause.errors.${detail}`)
      : t('pause.errors.unavailable');
  }
  // The backend words these itself ("We couldn't confirm the pause…"), so show them as is.
  if ((response?.status === 502 || response?.status === 503) && typeof detail === 'string') {
    if (detail.trim()) return detail;
  }
  return apiErrorMessage(err);
};

const BRAND_NAMES: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  american_express: 'American Express',
  discover: 'Discover',
  jcb: 'JCB',
  diners: 'Diners Club',
  diners_club: 'Diners Club',
  unionpay: 'UnionPay',
};

export const paymentMethodLines = (
  t: TFunction,
  pm: PortalPaymentMethod | null | undefined,
): { card: string; expires: string | null } | null => {
  if (!pm || !/^\d{4}$/.test(pm.last4)) return null;
  const brandKey = pm.brand?.trim().toLowerCase();
  const brand = brandKey ? (BRAND_NAMES[brandKey] ?? titleCase(brandKey)) : null;
  const card = brand
    ? t('subscriptionDetails.payment.cardEnding', { brand, last4: pm.last4 })
    : t('subscriptionDetails.payment.cardEndingNoBrand', { last4: pm.last4 });

  const { exp_month: month, exp_year: year } = pm;
  const hasExpiry =
    typeof month === 'number' && month >= 1 && month <= 12 && typeof year === 'number';
  const expires = hasExpiry
    ? t('subscriptionDetails.payment.expires', {
        date: `${String(month).padStart(2, '0')}/${String(year % 100).padStart(2, '0')}`,
      })
    : null;
  return { card, expires };
};
